import { useState, useEffect } from 'react';
import { superadminApi } from '../../api/superadminApi';
import { useAuth } from '../../context/AuthContext';
import '../../components/admin/AdminPanel.css';

// Styling-Objekt
const styles = {
    modalBody: { display: 'flex', minHeight: '500px', gap: '20px' },
    sidebar: { width: '200px', borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: '5px' },
    sidebarBtn: {
        textAlign: 'left', padding: '10px 15px', border: 'none', background: 'none',
        cursor: 'pointer', borderRadius: '6px', fontSize: '0.95rem', color: '#555', transition: '0.2s'
    },
    sidebarBtnActive: { background: '#eef2ff', color: '#4f46e5', fontWeight: '600' },
    sidebarBtnDanger: { color: '#dc2626' },
    contentArea: { flex: 1, padding: '0 10px', overflowY: 'auto' },
    sectionTitle: { marginTop: 0, borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px', color: '#333' }
};

function PlatformPage() {
    const { logout } = useAuth();
    const [companies, setCompanies] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [loading, setLoading] = useState(false);

    // MODAL STATES
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);

    // User Detail Modal State
    const [showUserDetailModal, setShowUserDetailModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editingUserTools, setEditingUserTools] = useState([]);
    const [availableCompanyTools, setAvailableCompanyTools] = useState([]);

    const [selectedCompany, setSelectedCompany] = useState(null);
    const [activeTab, setActiveTab] = useState('general');

    // DATA
    const [companyTools, setCompanyTools] = useState([]);
    const [companyUsers, setCompanyUsers] = useState([]);

    // SORT & SEARCH
    const [userSearch, setUserSearch] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

    // FORMS
    const [createForm, setCreateForm] = useState({
        companyName: '', adminName: '', adminUsername: '', adminPassword: '',
        maxUsers: 5, maxTotalUsers: 20, statusLockHours: 24
    });

    const [editForm, setEditForm] = useState({
        name: '', max_users: 5, max_total_users: 20, status_lock_hours: 24, is_active: true
    });

    const [adminEditForm, setAdminEditForm] = useState({
        newName: '', newUsername: '', newPassword: ''
    });

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        if (activeTab === 'users' && selectedCompany) {
            fetchCompanyUsers(selectedCompany.id);
        }
    }, [activeTab, selectedCompany]);

    const fetchCompanies = async () => {
        try {
            const data = await superadminApi.getCompanies();
            setCompanies(data);
        } catch (err) { console.error(err); }
    };

    const fetchCompanyUsers = async (companyId) => {
        try {
            const data = await superadminApi.getCompanyUsers(companyId);
            setCompanyUsers(data);
        } catch (err) { alert("Fehler beim Laden der Mitarbeiter"); }
    };

    // --- SORTIER LOGIK ---
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const getSortArrow = (name) => {
        if (sortConfig.key !== name) return '↕';
        return sortConfig.direction === 'asc' ? '⬆' : '⬇';
    };

    const getProcessedUsers = () => {
        const filtered = companyUsers.filter(u =>
            u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.username.toLowerCase().includes(userSearch.toLowerCase())
        );

        return filtered.sort((a, b) => {
            if (!sortConfig.key) return 0;

            let valA = a[sortConfig.key];
            let valB = b[sortConfig.key];

            if (valA === null || valA === undefined) valA = '';
            if (valB === null || valB === undefined) valB = '';

            if (sortConfig.key === 'last_status_change') {
                valA = valA ? new Date(valA).getTime() : 0;
                valB = valB ? new Date(valB).getTime() : 0;
            } else {
                valA = valA.toString().toLowerCase();
                valB = valB.toString().toLowerCase();
            }

            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    // --- USER EDITING LOGIC ---
    const openUserEdit = async (userId) => {
        try {
            const data = await superadminApi.getUserDetails(userId);
            setEditingUser(data.user);
            setEditingUserTools(data.userTools);
            setAvailableCompanyTools(data.companyTools);
            setShowUserDetailModal(true);
        } catch (err) {
            alert('Fehler beim Laden der User-Details');
        }
    };

    const saveUserBaseData = async (e) => {
        e.preventDefault();
        try {
            await superadminApi.updateUser(editingUser.id, editingUser);
            alert('✅ Daten erfolgreich gespeichert');
            fetchCompanyUsers(selectedCompany.id);
        } catch (err) { alert(err.response?.data?.error || 'Fehler'); }
    };

    const toggleUserTool = async (toolKey, currentStatus) => {
        const newStatus = !currentStatus;
        if (newStatus) setEditingUserTools([...editingUserTools, toolKey]);
        else setEditingUserTools(editingUserTools.filter(k => k !== toolKey));

        try {
            await superadminApi.updateUserTool(editingUser.id, toolKey, { grant: newStatus });
        } catch (err) {
            alert('Fehler beim Speichern des Tools');
        }
    };

    const forceUserStatus = async () => {
        const action = editingUser.is_active ? "deaktivieren" : "aktivieren";
        if (!window.confirm(`NOTFALL: Nutzer "${editingUser.name}" wirklich ${action}?`)) return;
        try {
            const data = await superadminApi.forceUserStatus(editingUser.id, { is_active: !editingUser.is_active });
            setEditingUser({ ...editingUser, is_active: data.is_active });
            fetchCompanyUsers(selectedCompany.id);
            alert(`Nutzer ist jetzt ${data.is_active ? 'AKTIV' : 'INAKTIV'}`);
        } catch (err) { alert('Fehler'); }
    };

    const resetUserPassword = async () => {
        const newPw = prompt("Neues Passwort für den Nutzer eingeben:");
        if (!newPw) return;
        try {
            await superadminApi.resetUserPassword(editingUser.id, { password: newPw });
            alert('✅ Passwort geändert');
        } catch (err) { alert('Fehler'); }
    };

    const deleteUser = async () => {
        if (!window.confirm("ACHTUNG: Nutzer wirklich endgültig löschen?")) return;
        try {
            await superadminApi.deleteUser(editingUser.id);
            alert('🗑️ Nutzer gelöscht');
            setShowUserDetailModal(false);
            fetchCompanyUsers(selectedCompany.id);
        } catch (err) { alert('Fehler'); }
    };

    // --- ACTIONS ---
    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await superadminApi.createCompany(createForm);
            alert('✅ Firma erfolgreich angelegt!');
            setShowCreateModal(false);
            setCreateForm({ companyName: '', adminName: '', adminUsername: '', adminPassword: '', maxUsers: 5, maxTotalUsers: 20, statusLockHours: 24 });
            fetchCompanies();
        } catch (err) { alert(err.response?.data?.error || 'Fehler beim Anlegen'); } finally { setLoading(false); }
    };

    const openEdit = async (comp) => {
        setSelectedCompany(comp);
        setActiveTab('general');
        setEditForm({ name: comp.name, max_users: comp.max_users, max_total_users: comp.max_total_users || 20, status_lock_hours: comp.status_lock_hours !== undefined ? comp.status_lock_hours : 24, is_active: comp.is_active });
        setAdminEditForm({ newName: comp.admin_name || '', newUsername: comp.admin_username || '', newPassword: '' });
        try {
            const data = await superadminApi.getCompanyTools(comp.id);
            setCompanyTools(data);
        } catch (err) { setCompanyTools([]); }
        setShowEditModal(true);
    };

    const openUserModal = () => {
        fetchCompanyUsers(selectedCompany.id);
        setUserSearch('');
        setSortConfig({ key: 'name', direction: 'asc' });
        setShowUserModal(true);
    };

    const handleUpdateGeneral = async (e) => {
        e.preventDefault();
        try {
            await superadminApi.updateCompany(selectedCompany.id, editForm);
            alert('✅ Stammdaten gespeichert!');
            fetchCompanies();
        } catch (err) { alert('Fehler'); }
    };

    const handleUpdateAdmin = async (e) => {
        e.preventDefault();
        if (!window.confirm("Admin-Daten wirklich speichern?")) return;
        try {
            await superadminApi.resetCompanyAdmin(selectedCompany.id, adminEditForm);
            alert('✅ Admin-Daten aktualisiert!');
            fetchCompanies();
        } catch (err) { alert(err.response?.data?.error || 'Fehler'); }
    };

    const handleSaveModules = async () => {
        try {
            await Promise.all(companyTools.map(tool =>
                superadminApi.updateCompanyTool(selectedCompany.id, tool.tool_key, { is_licensed: tool.is_licensed, enabled: tool.enabled })
            ));
            alert('✅ Module & Status gespeichert!');
        } catch (err) {
            console.error(err);
            alert("Fehler beim Speichern");
        }
    };

    const handleDeleteCompany = async () => {
        if (prompt(`Löschen bestätigen mit Name:`) !== selectedCompany.name) return;
        try {
            await superadminApi.deleteCompany(selectedCompany.id);
            alert('🗑️ Firma gelöscht.');
            setShowEditModal(false);
            fetchCompanies();
        } catch (err) { alert('Fehler'); }
    };

    return (
        <div className="app">
            <header className="app-header" style={{ background: '#2d3436' }}>
                <h1>🚀 Platform Control Center</h1>
                <div className="user-info">
                    <span style={{ fontWeight: 'bold', color: '#fab1a0' }}>SUPER ADMIN</span>
                    <button onClick={logout}>Logout</button>
                </div>
            </header>

            <main className="app-content" style={{ padding: '20px' }}>
                <div className="admin-panel" style={{ maxWidth: '1200px', margin: '0 auto' }}>

                    <div className="action-bar">
                        <div style={{ flex: 1 }}></div>
                        <button className="add-user-btn" onClick={() => setShowCreateModal(true)} style={{ background: '#764ba2' }}>+ Neuen Kunden anlegen</button>
                    </div>

                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Firma</th>
                                    <th>Haupt-Admin</th>
                                    {/* HIER GEÄNDERT: */}
                                    <th>Lizenzen / Speicher</th>
                                    <th>Erstellt am</th>
                                    <th style={{ textAlign: 'center' }}>Aktionen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {companies.map(c => (
                                    <tr key={c.id} style={{ opacity: c.is_active ? 1 : 0.6 }}>
                                        <td><span className={`status-badge ${c.is_active ? 'active' : 'inactive'}`}></span></td>
                                        <td style={{ fontWeight: 'bold' }}>{c.name}</td>
                                        <td>{c.admin_name || c.admin_username}</td>
                                        {/* --- NEUE SPALTE MIT 2 ZEILEN --- */}
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                                                {/* ZEILE 1: AKTIVE LIZENZEN (Farbig) */}
                                                <div style={{
                                                    fontSize: '0.9rem', fontWeight: '600',
                                                    color: (c.max_users !== -1 && c.active_user_count >= c.max_users) ? '#dc2626' : '#166534'
                                                }}>
                                                    <span style={{ marginRight: '4px' }}>Aktiv:</span>
                                                    {c.active_user_count} / {c.max_users === -1 ? '∞' : c.max_users}
                                                </div>

                                                {/* ZEILE 2: GESAMT SPEICHER (Grau) */}
                                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                    <span style={{ marginRight: '4px' }}>Gesamt:</span>
                                                    {c.user_count} / {!c.max_total_users || c.max_total_users === -1 ? '∞' : c.max_total_users}
                                                </div>

                                            </div>
                                        </td>
                                        <td>{new Date(c.created_at).toLocaleDateString()}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button className="action-btn" onClick={() => openEdit(c)} style={{ background: '#eef2ff', color: '#4f46e5' }}>⚙️ Verwalten</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* CREATE MODAL */}
                    {showCreateModal && (
                        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                            <div className="modal-content" onClick={e => e.stopPropagation()}>
                                <div className="modal-header"><h3>Neuen Mandanten anlegen</h3><button className="close-modal" onClick={() => setShowCreateModal(false)} style={{ color: '#333' }}>Zurück</button></div>
                                <div className="modal-body">
                                    <form onSubmit={handleCreate}>
                                        <h4 style={{ color: '#764ba2', marginTop: 0 }}>1. Firma</h4>
                                        <div className="form-group"><label>Name</label><input required value={createForm.companyName} onChange={e => setCreateForm({ ...createForm, companyName: e.target.value })} /></div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                            <div className="form-group"><label>Lizenz Limit</label><input type="number" required value={createForm.maxUsers} onChange={e => setCreateForm({ ...createForm, maxUsers: parseInt(e.target.value) })} /></div>
                                            <div className="form-group"><label>Account Limit</label><input type="number" required value={createForm.maxTotalUsers} onChange={e => setCreateForm({ ...createForm, maxTotalUsers: parseInt(e.target.value) })} /></div>
                                        </div>
                                        <div className="form-group"><label>Sperrfrist (h)</label><input type="number" required value={createForm.statusLockHours} onChange={e => setCreateForm({ ...createForm, statusLockHours: parseInt(e.target.value) })} /></div>
                                        <h4 style={{ color: '#764ba2' }}>2. Admin</h4>
                                        <div className="form-group"><label>Name</label><input required value={createForm.adminName} onChange={e => setCreateForm({ ...createForm, adminName: e.target.value })} /></div>
                                        <div className="form-group"><label>Username</label><input required value={createForm.adminUsername} onChange={e => setCreateForm({ ...createForm, adminUsername: e.target.value })} /></div>
                                        <div className="form-group"><label>Passwort</label><input required value={createForm.adminPassword} onChange={e => setCreateForm({ ...createForm, adminPassword: e.target.value })} /></div>
                                        <div className="modal-actions"><button type="submit" className="btn-save" style={{ background: '#764ba2' }}>Anlegen</button></div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* EDIT MODAL */}
                    {showEditModal && selectedCompany && (
                        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', width: '95%' }}>
                                <div className="modal-header"><h3>Verwaltung: {selectedCompany.name}</h3><button className="close-modal" onClick={() => setShowEditModal(false)} style={{ color: '#333' }}>Zurück</button></div>
                                <div style={styles.modalBody}>
                                    <div style={styles.sidebar}>
                                        <button style={{ ...styles.sidebarBtn, ...(activeTab === 'general' ? styles.sidebarBtnActive : {}) }} onClick={() => setActiveTab('general')}>🏢 Allgemein</button>
                                        <button style={{ ...styles.sidebarBtn }} onClick={openUserModal}>👥 Mitarbeiter (Liste)</button>
                                        <button style={{ ...styles.sidebarBtn, ...(activeTab === 'admin' ? styles.sidebarBtnActive : {}) }} onClick={() => setActiveTab('admin')}>👤 Admin</button>
                                        <button style={{ ...styles.sidebarBtn, ...(activeTab === 'modules' ? styles.sidebarBtnActive : {}) }} onClick={() => setActiveTab('modules')}>📦 Module</button>
                                        <div style={{ flex: 1 }}></div>
                                        <button style={{ ...styles.sidebarBtn, ...styles.sidebarBtnDanger, ...(activeTab === 'danger' ? { background: '#fef2f2' } : {}) }} onClick={() => setActiveTab('danger')}>☠️ Löschen</button>
                                    </div>
                                    <div style={styles.contentArea}>
                                        {activeTab === 'general' && (
                                            <form onSubmit={handleUpdateGeneral} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                <h4 style={styles.sectionTitle}>Stammdaten</h4>
                                                <div className="form-group"><label>Name</label><input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} /></div>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <div className="form-group" style={{ flex: 1 }}><label>Max. Aktiv</label><input type="number" value={editForm.max_users} onChange={e => setEditForm({ ...editForm, max_users: e.target.value })} /></div>
                                                    <div className="form-group" style={{ flex: 1 }}><label>Max. Gesamt</label><input type="number" value={editForm.max_total_users} onChange={e => setEditForm({ ...editForm, max_total_users: e.target.value })} /></div>
                                                </div>
                                                <div className="form-group"><label>Sperrfrist (h)</label><input type="number" value={editForm.status_lock_hours} onChange={e => setEditForm({ ...editForm, status_lock_hours: e.target.value })} /></div>
                                                <div className="form-group"><label>Status</label><select value={editForm.is_active} onChange={e => setEditForm({ ...editForm, is_active: e.target.value === 'true' })}><option value="true">Aktiv</option><option value="false">Gesperrt</option></select></div>
                                                <div style={{ marginTop: 'auto' }}><button type="submit" className="btn-save">Speichern</button></div>
                                            </form>
                                        )}
                                        {activeTab === 'admin' && <form onSubmit={handleUpdateAdmin}><h4 style={styles.sectionTitle}>Admin</h4><div className="form-group"><label>Name</label><input value={adminEditForm.newName} onChange={e => setAdminEditForm({ ...adminEditForm, newName: e.target.value })} /></div><div className="form-group"><label>Username</label><input value={adminEditForm.newUsername} onChange={e => setAdminEditForm({ ...adminEditForm, newUsername: e.target.value })} /></div><div className="form-group"><label>Passwort</label><input type="password" value={adminEditForm.newPassword} onChange={e => setAdminEditForm({ ...adminEditForm, newPassword: e.target.value })} /></div><button type="submit" className="btn-save">Speichern</button></form>}
                                        {/* --- TAB: MODULES (NEU: Lizenzen & Aktivierung) --- */}
                                        {activeTab === 'modules' && (
                                            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                <h4 style={styles.sectionTitle}>Module & Lizenzen verwalten</h4>

                                                {/* Erklärungstext */}
                                                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '15px', background: '#f9fafb', padding: '10px', borderRadius: '6px' }}>
                                                    <div>🔒 <b>Lizenz:</b> Hat der Kunde dafür bezahlt? (Bestimmt Besitz)</div>
                                                    <div>✅ <b>Aktiv:</b> Ist es im Dashboard eingeschaltet? (Bestimmt Sichtbarkeit)</div>
                                                </div>

                                                <div style={{ display: 'grid', gap: '10px', overflowY: 'auto', paddingBottom: '10px' }}>
                                                    {/* Tabellen-Kopfzeile */}
                                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '0 10px', fontSize: '0.85rem', fontWeight: 'bold', color: '#888' }}>
                                                        <span>Modul Name</span>
                                                        <span style={{ textAlign: 'center' }}>🔒 Lizenz</span>
                                                        <span style={{ textAlign: 'center' }}>✅ Aktiv</span>
                                                    </div>

                                                    {/* Liste der Tools */}
                                                    {companyTools.map(tool => (
                                                        <div key={tool.tool_key} style={{
                                                            display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', alignItems: 'center',
                                                            padding: '12px', borderRadius: '8px', border: '1px solid #eee',
                                                            background: tool.is_licensed ? '#fff' : '#f9fafb',
                                                            opacity: tool.is_licensed ? 1 : 0.6
                                                        }}>
                                                            {/* Spalte 1: Name */}
                                                            <div style={{ fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                {tool.tool_name}
                                                            </div>

                                                            {/* Spalte 2: Lizenz Checkbox */}
                                                            <div style={{ textAlign: 'center' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={tool.is_licensed}
                                                                    onChange={() => {
                                                                        // Wenn Lizenz weggenommen wird, muss auch "Aktiv" ausgehen
                                                                        const newLicensed = !tool.is_licensed;
                                                                        const updatedTools = companyTools.map(t =>
                                                                            t.tool_key === tool.tool_key
                                                                                ? { ...t, is_licensed: newLicensed, enabled: newLicensed ? t.enabled : false }
                                                                                : t
                                                                        );
                                                                        setCompanyTools(updatedTools);
                                                                    }}
                                                                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#764ba2' }}
                                                                />
                                                            </div>

                                                            {/* Spalte 3: Aktiv Checkbox (Nur klickbar wenn Lizenz da ist) */}
                                                            <div style={{ textAlign: 'center' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={tool.enabled}
                                                                    disabled={!tool.is_licensed} // Ausgegraut wenn keine Lizenz
                                                                    onChange={() => {
                                                                        const updatedTools = companyTools.map(t =>
                                                                            t.tool_key === tool.tool_key ? { ...t, enabled: !t.enabled } : t
                                                                        );
                                                                        setCompanyTools(updatedTools);
                                                                    }}
                                                                    style={{ width: '18px', height: '18px', cursor: tool.is_licensed ? 'pointer' : 'not-allowed' }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Speicher Button */}
                                                <div style={{ marginTop: 'auto', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                                                    <button
                                                        onClick={handleSaveModules}
                                                        className="btn-save"
                                                        style={{ background: '#10b981', width: '100%' }}
                                                    >
                                                        💾 Lizenzen & Status speichern
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {activeTab === 'danger' && <div style={{ textAlign: 'center' }}><h3 style={{ color: '#c33' }}>Firma Löschen</h3><button onClick={handleDeleteCompany} style={{ background: '#c33', color: 'white', padding: '10px', border: 'none', borderRadius: '6px' }}>Endgültig Löschen</button></div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* USER LIST MODAL */}
                    {showUserModal && selectedCompany && (
                        <div className="modal-overlay" onClick={() => setShowUserModal(false)} style={{ zIndex: 2000 }}>
                            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '1100px', width: '95%', height: '85vh', display: 'flex', flexDirection: 'column' }}>
                                <div className="modal-header">
                                    <div><h3 style={{ margin: 0 }}>Mitarbeiter: {selectedCompany.name}</h3></div>
                                    <button className="close-modal" onClick={() => setShowUserModal(false)} style={{ color: '#333' }}>Schließen</button>
                                </div>
                                <div style={{ padding: '15px', background: '#f9fafb', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between' }}>
                                    <input type="text" placeholder="Suchen..." value={userSearch} onChange={e => setUserSearch(e.target.value)} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '300px' }} />
                                    <span style={{ color: '#666' }}>{getProcessedUsers().length} Mitarbeiter</span>
                                </div>
                                <div className="modal-body" style={{ flex: 1, overflowY: 'auto', display: 'block', padding: 0 }}>
                                    <table className="user-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead style={{ background: '#f3f4f6', position: 'sticky', top: 0 }}>
                                            <tr>
                                                <th onClick={() => requestSort('name')} className="sortable-header" style={{ padding: '10px' }}>Name {getSortArrow('name')}</th>
                                                <th onClick={() => requestSort('role')} className="sortable-header">Systemberechtigung {getSortArrow('role')}</th>
                                                <th onClick={() => requestSort('last_status_change')} className="sortable-header">Status-Änderung {getSortArrow('last_status_change')}</th>
                                                <th onClick={() => requestSort('is_active')} className="sortable-header" style={{ textAlign: 'center' }}>Status {getSortArrow('is_active')}</th>
                                                <th style={{ textAlign: 'right', paddingRight: '20px' }}>Aktion</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getProcessedUsers().map(u => (
                                                <tr key={u.id} style={{ borderBottom: '1px solid #eee' }}>
                                                    <td style={{ padding: '10px' }}>
                                                        <div>{u.name}</div>
                                                        <div style={{ fontSize: '0.8rem', color: '#888' }}>{u.username}</div>
                                                    </td>
                                                    <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
                                                    <td style={{ fontSize: '0.85rem', color: '#555' }}>{u.last_status_change ? new Date(u.last_status_change).toLocaleString() : '-'}</td>
                                                    <td style={{ textAlign: 'center' }}><span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}></span></td>
                                                    <td style={{ textAlign: 'right', paddingRight: '20px' }}>
                                                        <button onClick={() => openUserEdit(u.id)} style={{ padding: '6px 12px', background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>✏️ Bearbeiten</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* USER DETAIL MODAL (Updated) */}
                    {showUserDetailModal && editingUser && (
                        <div className="modal-overlay" onClick={() => setShowUserDetailModal(false)} style={{ zIndex: 3000 }}>
                            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                                <div className="modal-header">
                                    <h3>Mitarbeiter bearbeiten: {editingUser.name}</h3>
                                    <button className="close-modal" onClick={() => setShowUserDetailModal(false)} style={{ color: '#333' }}>Schließen</button>
                                </div>
                                <div className="modal-body" style={{ display: 'block' }}>

                                    {/* FORMULAR START */}
                                    <form onSubmit={saveUserBaseData} style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #eee' }}>
                                        <h4 style={{ marginTop: 0, color: '#555' }}>Stammdaten</h4>
                                        <div className="form-group"><label>Name</label><input value={editingUser.name} onChange={e => setEditingUser({ ...editingUser, name: e.target.value })} /></div>
                                        <div className="form-group"><label>Username</label><input value={editingUser.username} onChange={e => setEditingUser({ ...editingUser, username: e.target.value })} /></div>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <div className="form-group" style={{ flex: 1 }}><label>Systemberechtigung</label>
                                                <select value={editingUser.role} onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}>
                                                    <option value="employee">Mitarbeiter</option><option value="manager">Manager</option><option value="admin">Admin</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* --- TOOLS SECTION JETZT HIER IM FORMULAR --- */}
                                        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                            <h4 style={{ marginTop: 0, color: '#555', fontSize: '1rem' }}>Tools zuweisen</h4>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                {availableCompanyTools.map(t => {
                                                    const hasTool = editingUserTools.includes(t.tool_key);
                                                    return (
                                                        <label key={t.tool_key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', borderRadius: '4px', background: hasTool ? '#ecfdf5' : '#f9fafb', border: '1px solid #eee', cursor: 'pointer' }}>
                                                            <input type="checkbox" checked={hasTool} onChange={() => toggleUserTool(t.tool_key, hasTool)} />
                                                            <span style={{ fontSize: '0.9rem', color: hasTool ? '#064e3b' : '#666' }}>{t.tool_name}</span>
                                                        </label>
                                                    )
                                                })}
                                                {availableCompanyTools.length === 0 && <div style={{ color: '#999', fontSize: '0.9rem' }}>Keine aktiven Unternehmenstools.</div>}
                                            </div>
                                        </div>

                                        <button type="submit" className="btn-save" style={{ padding: '8px', width: '100%' }}>💾 Änderungen Speichern</button>
                                    </form>
                                    {/* FORMULAR ENDE */}

                                    <div>
                                        <h4 style={{ marginTop: 0, color: '#c33' }}>Aktionen & Gefahrenzone</h4>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            <button onClick={forceUserStatus} style={{ padding: '10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold', background: editingUser.is_active ? '#fee2e2' : '#dcfce7', color: editingUser.is_active ? '#991b1b' : '#166534' }}>
                                                {editingUser.is_active ? '⛔ Zwang: Deaktivieren' : '✅ Zwang: Aktivieren'}
                                            </button>
                                            <button onClick={resetUserPassword} style={{ padding: '10px', background: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5', borderRadius: '6px', cursor: 'pointer' }}>🔑 Passwort Reset erzwingen</button>
                                            <button onClick={deleteUser} style={{ padding: '10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>🗑️ Nutzer endgültig löschen</button>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main >
        </div >
    );
}

export default PlatformPage;